import React from 'react';
import axios from 'axios';
import { getDomainForCategory } from '../utils/assetLabelHelper';

/**
 * AssetExpenseLinkModal — shared modal for linking a transaction to a physical asset expense
 *
 * Props:
 * - visible: boolean — whether the modal is shown
 * - onClose: () => void — close handler
 * - sourceType: 'cash' | 'revolver' — origin of the transaction
 * - transaction: object — minimally { id, description, expense_category, linked_asset_id }
 * - assetOptions: Array<{ id:number|string, label:string }> — list of selectable asset targets
 * - initialValues: { asset_id?: string|number, recurring?: boolean, frequency?: string } (optional)
 * - contextLabel: string — label to show in title (e.g., 'Structure or Land', 'Vehicle', 'Home Expense')
 * - onSaved: (payload: { asset_id:number, recurring:boolean, recurring_frequency:string|null }) => Promise|void
 * - onUnlinked: () => Promise|void
 */
export default function AssetExpenseLinkModal({
  visible,
  onClose,
  sourceType,
  transaction,
  assetOptions = [],
  initialValues = {},
  contextLabel = 'Asset',
  onSaved,
  onUnlinked,
  // New optional domain support: 'generic' | 'home' | 'landscaping' | 'waste' | 'vehicle' | 'utilities'
  domain = 'generic',
  // For utilities/media domains
  utilitiesConfig = {
    propertyId: null, // residential property id to load existing series (include_children=1)
    categories: [
      { value: 'utilities_electric_gas', label: 'Electric & Gas' },
      { value: 'utilities_water', label: 'Water' },
      { value: 'utilities_trash', label: 'Trash/Waste' },
      { value: 'utilities_sewer', label: 'Sewer' },
      { value: 'utilities_internet', label: 'Internet' },
      { value: 'utilities_cable_tv', label: 'Cable/TV' },
      { value: 'utilities_phone', label: 'Phone' },
    ],
  },
  mediaConfig = {
    categories: [
      { value: 'media_internet', label: 'Internet' },
      { value: 'media_cable_tv', label: 'Cable/TV' },
      { value: 'media_phone', label: 'Phone' },
      { value: 'media_streaming', label: 'Streaming Services' },
      { value: 'media_books', label: 'Books' },
      { value: 'media_other', label: 'Other Media' },
    ],
  },
}) {
  const initialValuesMemo = React.useMemo(() => initialValues, [initialValues]);
  const transactionMemo = React.useMemo(() => transaction, [transaction]);
  const [selectedAssetId, setSelectedAssetId] = React.useState('');
  const [recurring, setRecurring] = React.useState(false);
  const [frequency, setFrequency] = React.useState('monthly');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [propertyOptions, setPropertyOptions] = React.useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = React.useState('');
  const [internalAssetOptions, setInternalAssetOptions] = React.useState([]);
  const [selectedAssetDetail, setSelectedAssetDetail] = React.useState(null);
  const [assetDetailLoading, setAssetDetailLoading] = React.useState(false);
  const [selectedTodoId, setSelectedTodoId] = React.useState('');
  const [todoOptions, setTodoOptions] = React.useState([]);
  const [utilCategory, setUtilCategory] = React.useState('');
  const [recurringMode, setRecurringMode] = React.useState('existing'); // 'existing' | 'new'
  const [seriesOptions, setSeriesOptions] = React.useState([]); // [{id,name, asset_id, type, last_frequency, expense_category}]
  const [seriesLoading, setSeriesLoading] = React.useState(false);
  const [existingSeriesId, setExistingSeriesId] = React.useState('');
  const [newSeriesName, setNewSeriesName] = React.useState('');
  const [newSeriesType, setNewSeriesType] = React.useState('variable');
  const [metaLoading, setMetaLoading] = React.useState(false);
  const [userPickedSeries, setUserPickedSeries] = React.useState(false);
  const [billLoading, setBillLoading] = React.useState(false);
  const [billError, setBillError] = React.useState('');
  const [billOptions, setBillOptions] = React.useState([]); // [{id, provider, service_start, service_end, total_amount}]
  const [selectedBillId, setSelectedBillId] = React.useState('');
  const [userPickedBill, setUserPickedBill] = React.useState(false);
  const [vehicleCategory, setVehicleCategory] = React.useState('');
  const [isEditingVehicleCategory, setIsEditingVehicleCategory] = React.useState(false);
  const [effectivelyUnlinkedAsset, setEffectivelyUnlinkedAsset] = React.useState(false);
  const [effectivelyUnlinkedSeries, setEffectivelyUnlinkedSeries] = React.useState(false);
  const [effectivelyUnlinkedBill, setEffectivelyUnlinkedBill] = React.useState(false);
  const [isEditingUtilCategory, setIsEditingUtilCategory] = React.useState(false);

  // Use refs to stabilize input props that might be unstable in parent
  const initialValuesRef = React.useRef(initialValues);
  const transactionRef = React.useRef(transaction);
  const assetOptionsRef = React.useRef(assetOptions);
  const utilitiesConfigRef = React.useRef(utilitiesConfig);
  const mediaConfigRef = React.useRef(mediaConfig);

  React.useEffect(() => {
    initialValuesRef.current = initialValues;
    transactionRef.current = transaction;
    assetOptionsRef.current = assetOptions;
    utilitiesConfigRef.current = utilitiesConfig;
    mediaConfigRef.current = mediaConfig;
  }, [initialValues, transaction, assetOptions, utilitiesConfig, mediaConfig]);

  const seriesOptionsMemo = React.useMemo(() => seriesOptions, [seriesOptions]);
  const billOptionsMemo = React.useMemo(() => billOptions, [billOptions]);
  const assetOptionsMemo = React.useMemo(() => assetOptions, [assetOptions]);
  const internalAssetOptionsMemo = React.useMemo(() => internalAssetOptions, [internalAssetOptions]);
  const propertyOptionsMemo = React.useMemo(() => propertyOptions, [propertyOptions]);
  const utilitiesConfigMemo = React.useMemo(() => utilitiesConfig, [utilitiesConfig]);
  const mediaConfigMemo = React.useMemo(() => mediaConfig, [mediaConfig]);
  const linkageOptionsCacheRef = React.useRef(new Map());
  // Residential domain extra
  const isMedia = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'media') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'media';
  }, [domain]);
  const isUtilities = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'utilities') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'utilities';
  }, [domain]);
  const isTaxes = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'taxes') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'taxes';
  }, [domain]);
  const isHome = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'home') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'home';
  }, [domain]);
  const isLandscaping = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'landscaping') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'landscaping';
  }, [domain]);
  const isVehicle = React.useMemo(() => {
    const d = String(domain).toLowerCase();
    if (d === 'vehicle') return true;
    const cat = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '').toLowerCase();
    return getDomainForCategory(cat) === 'vehicle';
  }, [domain]);
  const isResidential = React.useMemo(() => isUtilities || isTaxes || isHome || isLandscaping || isMedia || (domain && String(domain).toLowerCase() === 'generic') || (transactionRef.current?.expense_category && getDomainForCategory(transactionRef.current.expense_category) !== 'vehicle'), [isUtilities, isTaxes, isHome, isLandscaping, isMedia, domain]);
  const isPhysicalAsset = React.useMemo(() => isResidential || isVehicle || domain === 'generic', [isResidential, isVehicle, domain]);
  const initialPropertyFetchDoneRef = React.useRef(false);
  // Track if the user has manually changed the property to avoid auto-clearing
  const userManuallySelectedPropertyRef = React.useRef(false);
  const userManuallySelectedSeriesRef = React.useRef(false);
  const userManuallySelectedBillRef = React.useRef(false);
  const userManuallyToggledRecurringRef = React.useRef(false);
  const seriesCacheRef = React.useRef(new Map());
  const billsCacheRef = React.useRef(new Map());
  // Initial mount ref to avoid double-firing or cycles on first render
  const isInitializedRef = React.useRef(false);

  // Use a ref to track if we've already done the initial property fetch to avoid infinite loops
  React.useEffect(() => {
    if (!visible) {
      initialPropertyFetchDoneRef.current = false;
      userManuallySelectedPropertyRef.current = false;
      userManuallySelectedSeriesRef.current = false;
      userManuallySelectedBillRef.current = false;
      userManuallyToggledRecurringRef.current = false;
      isInitializedRef.current = false;
      setSeriesLoading(false);
      setBillLoading(false);
      setBusy(false);
    }
  }, [visible]);



  // Load asset detail for better labels and To-Do items when selected
  React.useEffect(() => {
    const aid = String(selectedAssetId || transactionRef.current?.linked_asset_id || initialValuesRef.current?.asset_id || '');
    if (!aid || !visible) {
      setSelectedAssetDetail(null);
      setTodoOptions([]);
      return;
    }
    
    // Check if we already have it in options and it has a good label and TODOs
    const cacheKey = `asset:${aid}`;
    if (linkageOptionsCacheRef.current.has(cacheKey)) {
        const cached = linkageOptionsCacheRef.current.get(cacheKey);
        // Ensure the cache actually has todo_items before returning
        if (cached.todo_items) {
          setSelectedAssetDetail(cached.parent || cached);
          setTodoOptions(cached.todo_items || []);
          const targetId = (transactionRef.current?.linked_todo_id != null ? String(transactionRef.current.linked_todo_id) : (initialValuesRef.current?.todo_id != null ? String(initialValuesRef.current.todo_id) : ''));
          if (targetId && cached.todo_items?.some(t => String(t.id) === targetId)) {
            if (String(selectedTodoId) !== targetId) setSelectedTodoId(targetId);
          }
          return;
        }
    }

    const options = (assetOptionsMemo.length ? assetOptionsMemo : internalAssetOptionsMemo);
    const opt = options.find(o => String(o.id) === String(aid));
    if (opt && opt.todo_items && opt.todo_items.length > 0 && (opt.address || opt.vehicle_year || opt.year || (opt.label && !opt.label.startsWith('Asset ') && !opt.label.startsWith('Structure ') && !opt.label.startsWith('Vehicle ')))) {
        setSelectedAssetDetail(opt);
        setTodoOptions(opt.todo_items || []);
        
        // Ensure pre-population even if we use cached option
        const txnTodoId = (transactionRef.current && transactionRef.current.linked_todo_id != null) ? String(transactionRef.current.linked_todo_id) : '';
        const initTodoId = (initialValuesRef.current && initialValuesRef.current.todo_id != null) ? String(initialValuesRef.current.todo_id) : '';
        const targetId = txnTodoId || initTodoId;
        if (targetId && opt.todo_items?.some(t => String(t.id) === targetId)) {
          if (String(selectedTodoId) !== targetId) {
            setSelectedTodoId(targetId);
          }
        }
        return;
    }

    const fetchDetail = async () => {
      try {
        setAssetDetailLoading(true);
        // We use the linkage-options endpoint which provides the tree/context for the asset
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${aid}/linkage-options/`);
        // Payload contains { asset_id, parent: { id, label, property_id, ... }, children: [...] }
        if (res.data && String(res.data.asset_id) === String(aid)) {
          setSelectedAssetDetail(res.data.parent || res.data);
          setTodoOptions(res.data.todo_items || []);

          // Pre-populate todo ID if it matches any in the fetched options
          const txnTodoId = (transactionRef.current && transactionRef.current.linked_todo_id != null) ? String(transactionRef.current.linked_todo_id) : '';
          const initTodoId = (initialValuesRef.current && initialValuesRef.current.todo_id != null) ? String(initialValuesRef.current.todo_id) : '';
          const targetId = txnTodoId || initTodoId;
          if (targetId && res.data.todo_items?.some(t => String(t.id) === targetId)) {
            if (String(selectedTodoId) !== targetId) {
              setSelectedTodoId(targetId);
            }
          }
        
          // Use cached linkage-options if available to avoid redundant calls
          const cacheKey = `asset:${aid}`;
          linkageOptionsCacheRef.current.set(cacheKey, res.data);
        } else {
          setSelectedAssetDetail(null);
        }
      } catch (e) {
        setSelectedAssetDetail(null);
      } finally {
        setAssetDetailLoading(false);
      }
    };
    fetchDetail();
  }, [visible, isResidential, selectedAssetId, assetOptionsMemo, internalAssetOptionsMemo]); // Removed selectedPropertyId and selectedTodoId to prevent infinite loop

  // Effect specifically for syncing property ID from asset details
  React.useEffect(() => {
    if (visible && isResidential && !selectedPropertyId && selectedAssetDetail) {
      const pid = selectedAssetDetail.property_id || (selectedAssetDetail.parent && selectedAssetDetail.parent.id && selectedAssetDetail.parent.type === 'ResidentialProperty' ? selectedAssetDetail.parent.id : null);
      if (pid && String(selectedPropertyId) !== String(pid)) {
        setSelectedPropertyId(String(pid));
      }
    }
  }, [visible, isResidential, selectedPropertyId, selectedAssetDetail]);



  // Helper: series list eligible for the current selection context
  const getContextSeries = React.useCallback(() => {
    try {
      const list = Array.isArray(seriesOptions) ? seriesOptions : [];
      const aid = String(selectedAssetId || initialValuesRef.current?.asset_id || transactionRef.current?.linked_asset_id || '');
      if (!aid) return isVehicle ? list : [];

      if (isVehicle) {
        // Double check filtering for vehicles as requested by user
        // Note: vehicle series fetch already filters by vehicle id in URL
        return list.filter(s => String(s.asset_id || s.linked_asset_id || '') === aid);
      }

      if (!isResidential) {
        return list.filter(s => String(s.asset_id || s.linked_asset_id || '') === aid);
      }
      const pid = String(utilitiesConfigRef.current?.propertyId || selectedPropertyId || initialValuesRef.current?.property_id || initialValuesRef.current?.linked_property_id || '');
      const out = list.filter(s => {
        const a = String(s.asset_id || s.linked_asset_id || '');
        const p = String(s.property_id || s.linked_property_id || '');
        if (aid && a === aid) return true;
        // If the selected asset is the Property itself, show all its child series too
        if (aid && aid === pid) return true;
        if (pid && (a === pid || p === pid)) return true; // allow property-level series for all residential assets
        return false;
      });
      return out;
    } catch (e) {
      return [];
    }
  }, [seriesOptions, selectedAssetId, isResidential, isVehicle, selectedPropertyId]);

  // Format helpers
  const prettyFreq = (v) => {
    const m = String(v || '').toLowerCase();
    if (!m) return '';
    return m.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase());
  };
  const prettyType = (v) => {
    const t = String(v || '').toLowerCase();
    if (!t) return '';
    return t.replace(/\b\w/g, c=>c.toUpperCase());
  };

  React.useEffect(() => {
    if (!visible) return;
    if (busy) return; 
    setError('');
    setEffectivelyUnlinkedAsset(false);
    setEffectivelyUnlinkedSeries(false);
    setEffectivelyUnlinkedBill(false);
    setIsEditingUtilCategory(false);
    setIsEditingVehicleCategory(false);
    setUserPickedSeries(false);
    setUserPickedBill(false);
    // When re-opening or first opening, if no existing series is linked, ensure we don't have a stale value
    const initSeriesId = (initialValuesRef.current && initialValuesRef.current.recurring_series_id != null) ? String(initialValuesRef.current.recurring_series_id) : '';
    const txnSeriesId = (transactionRef.current && transactionRef.current.linked_recurring_series_id != null) ? String(transactionRef.current.linked_recurring_series_id) : '';
    if (!txnSeriesId && !initSeriesId && !userPickedSeries && !userManuallySelectedSeriesRef.current) {
       if (existingSeriesId !== '') setExistingSeriesId('');
    }
    const txnTodoId = (transactionRef.current && transactionRef.current.linked_todo_id != null) ? String(transactionRef.current.linked_todo_id) : '';
    const initTodoId = (initialValuesRef.current && initialValuesRef.current.todo_id != null) ? String(initialValuesRef.current.todo_id) : '';
    if (!txnTodoId && !initTodoId) {
      if (selectedTodoId !== '') {
        setSelectedTodoId('');
      }
    }
  }, [visible, busy]); // Removed selectedTodoId

  React.useEffect(() => {
    if (!visible) return;
    if (busy) return;
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;
    try {
      const targetAssetId = String(
          initialValuesRef.current.asset_id != null
            ? initialValuesRef.current.asset_id
            : (transactionRef.current?.linked_asset_id != null ? transactionRef.current.linked_asset_id : '')
      );
      if (selectedAssetId !== targetAssetId) {
        setSelectedAssetId(targetAssetId);
      }
      // Auto-enable recurring ONLY if a specific series is already linked or transaction is marked as recurring
      const initSeriesId = (initialValuesRef.current && initialValuesRef.current.recurring_series_id != null) ? String(initialValuesRef.current.recurring_series_id) : '';
      const txnSeriesId = (transactionRef.current && transactionRef.current.linked_recurring_series_id != null) ? String(transactionRef.current.linked_recurring_series_id) : '';
      const hasSeries = !!(initSeriesId || txnSeriesId);
      const txnRecurring = !!(transactionRef.current && transactionRef.current.linked_expense_info && transactionRef.current.linked_expense_info.recurring === true);
      const shouldBeRecurring = hasSeries || txnRecurring || !!initialValuesRef.current.recurring;

      if (!userPickedSeries && !userManuallyToggledRecurringRef.current) {
        setRecurring(shouldBeRecurring);
      }
      setFrequency(initialValuesRef.current.frequency || 'monthly');

      // Pre-populate todo ID
      const txnTodoId = (transactionRef.current && transactionRef.current.linked_todo_id != null) ? String(transactionRef.current.linked_todo_id) : '';
      const initTodoId = (initialValuesRef.current && initialValuesRef.current.todo_id != null) ? String(initialValuesRef.current.todo_id) : '';
      if (txnTodoId || initTodoId) {
        const targetId = txnTodoId || initTodoId;
        if (String(selectedTodoId) !== targetId) {
          setSelectedTodoId(targetId);
        }
      }

      // Pre-set property ID if available
      if (isResidential) {
        const pid = initialValuesRef.current?.property_id || initialValuesRef.current?.linked_property_id;
        if (pid) {
          const pidStr = String(pid);
          if (String(selectedPropertyId) !== pidStr) setSelectedPropertyId(pidStr);
        } else if (isResidential && propertyOptions.length === 0 && !initialPropertyFetchDoneRef.current) {
          // If no property is preselected for residential domains, load the list of properties immediately
          // so the user can see choices as requested in Expenses page context.
          (async () => {
            try {
              initialPropertyFetchDoneRef.current = true;
              const res = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
              const props = Array.isArray(res.data) ? res.data : [];
              setPropertyOptions(props);
              
              // If there's only one property, auto-select it to show its structures/land
              if (props.length === 1 && props[0].id) {
                const pid = String(props[0].id);
                if (String(selectedPropertyId) !== pid) {
                  setSelectedPropertyId(pid);
                }
              }
            } catch (e) {
              setPropertyOptions([]);
            }
          })();
        }
      }

      // Initialize utilities/media defaults
      if (isUtilities || isMedia) {
        // If we have an explicit property id from caller, set it immediately so loaders use it
        // If caller passed an initial property for utilities (e.g., derived from linked asset), preselect it
        try {
          const initPropId = String(initialValues?.property_id || '');
          if (!utilitiesConfig?.propertyId && initPropId) {
            if (String(selectedPropertyId) !== initPropId) setSelectedPropertyId(initPropId);
            // Also attempt to load structures list for that property immediately (cached)
            (async () => {
              try {
                const cacheKey = `prop:${initPropId}`;
                if (linkageOptionsCacheRef.current.has(cacheKey)) {
                  const structs = linkageOptionsCacheRef.current.get(cacheKey);
                  setInternalAssetOptions(structs);
                } else {
                  const resp = await axios.get(`/api/balance_sheet/assets/physical_assets/${initPropId}/linkage-options/`);
                  const root = resp?.data || {};
                  const children = Array.isArray(root.children) ? root.children : [];
                  const structs = children
                    .filter(ch => String((ch.type || '').toLowerCase()) !== 'land')
                    .map(ch => ({ id: ch.id, label: ch.label || `Structure ${ch.id}` }));
                  linkageOptionsCacheRef.current.set(cacheKey, structs);
                  setInternalAssetOptions(structs);
                }
              } catch (e) {
                setInternalAssetOptions([]);
              }
            })();
          }
        } catch (e) { /* ignore prefill errors */ }
        const defaultCat = isMedia 
          ? (mediaConfig?.categories || [])[0]?.value || ''
          : (utilitiesConfig?.categories || [])[0]?.value || '';
        // Prefer explicitly provided utilities sub-category from parent initial values
        const raw = String(initialValues?.expense_category || transaction?.expense_category || '').trim();
        let normalized = '';
        // Case 1: canonical value already
        if (raw.startsWith('utilities_') || raw.startsWith('media_')) {
          normalized = raw;
        } else if (raw) {
          // Case 2: attempt to map from label-like strings such as
          // "utilities - water", "Utilities – Water", or similar
          // "Media - TV", "Media - Internet"
          const lower = raw.toLowerCase().replace(/[_\s–—-]+/g, ' ').trim();
          const cats = isMedia 
            ? (Array.isArray(mediaConfig?.categories) ? mediaConfig.categories : [])
            : (Array.isArray(utilitiesConfig?.categories) ? utilitiesConfig.categories : []);
          
          // Special cases for common short names
          let targetLabel = lower;
          if (lower === 'tv') targetLabel = 'cable/tv';

          for (const c of cats) {
            const lbl = String(c.label || '').toLowerCase();
            // match when label text appears in raw (e.g., 'water', 'electric', 'phone', 'tv', 'internet')
            if (lbl && (targetLabel.includes(lbl) || targetLabel.endsWith(lbl))) {
              normalized = c.value;
              break;
            }
          }
        }
        if (!busy) setUtilCategory(normalized || defaultCat);
        if (!busy) setRecurringMode('existing');
        // Don't reset existing series if user already picked one in this session or we're busy
        if (!userPickedSeries && !busy) setExistingSeriesId(String(initSeriesId || txnSeriesId || ''));
        setNewSeriesName('');
        setNewSeriesType('variable');
        setBillError('');
        // Only apply initial value if user hasn't already picked one during this open session
        if (!userPickedBill) {
          const initBill = (initialValuesMemo && initialValuesMemo.utility_bill_id != null) ? String(initialValuesMemo.utility_bill_id) : '';
          const txnBill = (transactionMemo && transactionMemo.linked_utility_bill_id != null) ? String(transactionMemo.linked_utility_bill_id) : '';
          setSelectedBillId(String(initBill || txnBill || ''));
        }
        // When no propertyId is provided, clear any residual internal options and selected property
        // ONLY if this is the initial mount/opening and no initial property is provided.
        if (!utilitiesConfigMemo?.propertyId && !initialPropertyFetchDoneRef.current && !userManuallySelectedPropertyRef.current) {
          if (!initialValuesMemo?.property_id && !transactionMemo?.linked_property_id) {
            if (selectedPropertyId) setSelectedPropertyId('');
            if (internalAssetOptions.length) setInternalAssetOptions([]);
          }
          initialPropertyFetchDoneRef.current = true;
        }
      } else if (isTaxes) {
        if (!userPickedSeries && !busy) setExistingSeriesId(String(initSeriesId || txnSeriesId || ''));
        if (!busy) setRecurringMode('existing');
        setNewSeriesName('');
      } else if (isHome || isLandscaping) {
        // Basic prefill for Home/Landscaping
        if (!userPickedSeries && !busy) setExistingSeriesId(String(initSeriesId || txnSeriesId || ''));
        if (!busy) setRecurringMode('existing');
        setNewSeriesName('');
      } else if (isVehicle) {
        // Vehicle logic
        const raw = String(initialValuesMemo?.expense_category || transactionMemo?.expense_category || '').trim();
        const lower = raw.toLowerCase().replace(/[_\s–—-]+/g, ' ').trim();
        
        let normalized = '';
        if (lower.includes('gas') || lower.includes('fuel')) {
          normalized = 'asset_input';
        } else if (raw && [
          'initial_purchase', 'maintenance', 'repair', 'enhancement', 'taxes', 
          'insurance', 'ticket_or_fine', 'asset_input', 'asset_storage', 'financing_interest', 'financing_principal',
          'licensing_tags', 'automotive', 'gas_fuel', 'vehicle'
        ].includes(raw)) {
          normalized = (raw === 'gas_fuel' ? 'asset_input' : (['automotive', 'vehicle'].includes(raw) ? 'maintenance' : raw));
        } else {
          normalized = 'maintenance'; // default
        }
        
        if (!busy) setVehicleCategory(normalized);
        if (!userPickedSeries && !busy) setExistingSeriesId(String(initSeriesId || txnSeriesId || ''));
        if (!busy) setRecurringMode('existing');
        setNewSeriesName('');
      }
    } catch (e) {
      if (!userPickedSeries && !userManuallyToggledRecurringRef.current) {
        setSelectedAssetId('');
        setRecurring(false);
        setFrequency('monthly');
      }
      setError('');
    }
  }, [
    visible,
    busy,
    isResidential,
    isUtilities,
    isMedia,
    isTaxes,
    isHome,
    isLandscaping,
    isVehicle,
    selectedPropertyId,
    userPickedSeries,
    userPickedBill,
  ]); // Removed selectedTodoId and lengths

  // Load linked AssetExpense detail (series + bill) deterministically when available
  React.useEffect(() => {
    let cancelled = false;
    const fetchExpenseDetail = async () => {
      try {
        if (!visible || busy || (!isResidential && !isVehicle)) return;
        const expIdRaw = transaction?.linked_asset_expense_id ?? initialValues?.linked_asset_expense_id ?? transaction?.linkedAssetExpenseId;
        const expId = expIdRaw != null ? String(expIdRaw) : '';
        if (!expId) return;
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/expenses/${expId}/detail/`);
        if (cancelled) return;
        const d = res?.data || {};
        // Prefer detail-derived ids for robust preselects
        const sid = (d?.recurring_series?.id != null ? String(d.recurring_series.id) : (d?.recurringSeriesId != null ? String(d.recurringSeriesId) : ''));
        const bid = (d?.utility_bill?.id != null ? String(d.utility_bill.id) : (d?.utilityBillId != null ? String(d.utilityBillId) : ''));
        if (sid && !busy) {
          if (existingSeriesId !== sid) setExistingSeriesId(sid);
          if (!userPickedSeries && !userManuallyToggledRecurringRef.current) {
            if (!recurring) setRecurring(true);
            if (recurringMode !== 'existing') setRecurringMode('existing');
          }
        }
        if ((isUtilities || isMedia) && bid && !busy) {
          if (selectedBillId !== bid) setSelectedBillId(bid);
        }
        if (isVehicle && (d?.expense_category || d?.expenseCategory) && !busy) {
          const vcat = d.expense_category || d.expenseCategory;
          if (vehicleCategory !== vcat) setVehicleCategory(vcat);
        }
        // Also reinforce property/asset if provided
        if ((d?.property_id || d?.propertyId) && !busy) {
          const pid = String(d.property_id || d.propertyId);
          if (selectedPropertyId !== pid) setSelectedPropertyId(pid);
        }
        if ((d?.asset_id || d?.assetId) && !busy) {
          const aid = String(d.asset_id || d.assetId);
          if (selectedAssetId !== aid) setSelectedAssetId(aid);
        }
      } catch (e) {
        // ignore; fallback to existing hints
      }
    };
    fetchExpenseDetail();
    return () => { cancelled = true; };
  }, [
    visible,
    busy,
    isResidential,
    isVehicle,
    isUtilities,
    isMedia,
    selectedPropertyId,
    selectedAssetId,
    userPickedSeries,
  ]);

  // When opening from source pages (Cash/Expenses/Credit) without an explicit propertyId,
  // derive the Residential Property from the linked asset id and load structures so the
  // selected structure and series/bill context can prefill correctly.
  React.useEffect(() => {
    const hydrateFromLinkedAsset = async () => {
      try {
        if (!visible || busy || !isResidential) return;
        // If caller provided propertyId (via initialValues or utilitiesConfig), prefer that and just load structures
        const initProp = String(initialValues?.property_id || utilitiesConfig?.propertyId || '');
        if (initProp) {
          if (!selectedPropertyId) setSelectedPropertyId(initProp);
          // Load structures for the provided property id (cached)
          try {
            const cacheKey = `prop:${initProp}`;
            if (linkageOptionsCacheRef.current.has(cacheKey)) {
              setInternalAssetOptions(linkageOptionsCacheRef.current.get(cacheKey));
            } else {
              const optRes = await axios.get(`/api/balance_sheet/assets/physical_assets/${initProp}/linkage-options/`);
              const root = optRes?.data || {};
              const children = Array.isArray(root.children) ? root.children : [];
              const options = [];
              // Add entire property option
              if (root.id) {
                const plabel = root.label || (root.address ? `${root.address} - Property` : `Residential Property - ${initProp}`);
                options.push({ id: root.id, label: plabel });
              }
              // Add all children (structures and land)
              for (const ch of children) {
                if (!ch || !ch.id) continue;
                const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
                options.push({ id: ch.id, label: lbl });
              }
              linkageOptionsCacheRef.current.set(cacheKey, options);
              setInternalAssetOptions(options);
            }
          } catch (e) {
            setInternalAssetOptions([]);
          }
          // Ensure the linked asset is selected if provided
          const aid0 = String(initialValues?.asset_id || transaction?.linked_asset_id || '');
          if (aid0 && String(selectedAssetId) !== aid0) setSelectedAssetId(aid0);
          return;
        }
        if (selectedPropertyId) return; // already derived/selected
        const aid = String(initialValues?.asset_id || transaction?.linked_asset_id || '');
        if (!aid) return;
        // Fetch linkage options for this asset id to determine its parent Residential Property
        // Note: There is no detail endpoint; use linkage-options which returns the tree with parent context
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${aid}/linkage-options/`);
        const data = res?.data || {};
        let propId = data.property_id ? String(data.property_id) : '';
        // linkage-options: prefer explicit property_id from backend; fallback to parent traversal
        if (!propId && data && data.parent && (data.parent.id || data.parent.pk)) {
          const pType = String(data.parent.type || '').toLowerCase();
          if (pType === 'resi_property' || pType === 'property' || pType === 'residential_property') {
            propId = String(data.parent.id || data.parent.pk);
          }
        }
        // If not found, and root looks like a property node with children, use its id
        if (!propId && data && data.id && Array.isArray(data.children) && data.children.length > 0) {
          propId = String(data.id);
        }
                      // If still not found, try a series-based inference: query recurring-series for the linked asset id
        if (!propId) {
          try {
            const serResp = await axios.get(`/api/balance_sheet/assets/physical_assets/${aid}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
            const items = Array.isArray(serResp?.data) ? serResp.data : [];
            // If any item lists an asset_id that is a property id (your backend stores property-scoped series), use that
            const any = items.find(s => s && s.asset_id != null);
            if (any && any.asset_id != null) propId = String(any.asset_id);
          } catch (e) { /* ignore */ }
        }
        if (propId) {
          setSelectedPropertyId(propId);
          // Load structures for that property so the asset dropdown shows the linked structure
          try {
            const cacheKey = `prop:${propId}`;
            if (linkageOptionsCacheRef.current.has(cacheKey)) {
              setInternalAssetOptions(linkageOptionsCacheRef.current.get(cacheKey));
            } else {
              const optRes = await axios.get(`/api/balance_sheet/assets/physical_assets/${propId}/linkage-options/`);
              const root = optRes?.data || {};
              const children = Array.isArray(root.children) ? root.children : [];
              const options = [];

              if (isLandscaping) {
                // For landscaping, we only want the Land asset in a unified list
                const land = children.find(ch => String(ch.type).toLowerCase() === 'land');
                if (land) {
                  // Attempt to get property address for nice label
                  let addr = propId;
                  try {
                    const pChoice = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
                    const match = (pChoice.data || []).find(p => String(p.id) === propId);
                    if (match) addr = match.address;
                  } catch (e) {}
                  options.push({ id: land.id, label: `${addr || 'Property'} - Land`, propertyId: propId });
                }
              } else {
                // Add entire property option
                if (root.id) {
                  const plabel = root.label || (root.address ? `${root.address} - Property` : `Residential Property - ${propId}`);
                  options.push({ id: root.id, label: plabel });
                }
                // Add all children (structures and land)
                for (const ch of children) {
                  if (!ch || !ch.id) continue;
                  const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
                  options.push({ id: ch.id, label: lbl });
                }
              }
              linkageOptionsCacheRef.current.set(cacheKey, options);
              setInternalAssetOptions(options);
            }
          } catch (e) {
            setInternalAssetOptions([]);
          }
        } else if (isLandscaping && !initialPropertyFetchDoneRef.current) {
          // If no property is preselected for landscaping, load properties so user can choose
          (async () => {
             try {
               initialPropertyFetchDoneRef.current = true;
               const res = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
               const props = Array.isArray(res.data) ? res.data : [];
               setPropertyOptions(props);
               if (props.length === 1 && props[0].id) {
                 const pid = String(props[0].id);
                 setSelectedPropertyId(pid);
                 // The useEffect will trigger again with selectedPropertyId set
               }
             } catch(e){}
          })();
        } else {
          // Fallback: try to discover the property by scanning properties for the linked recurring series id
          const targetSeriesId = String((initialValues && initialValues.recurring_series_id != null) ? initialValues.recurring_series_id : (transaction && transaction.linked_recurring_series_id != null ? transaction.linked_recurring_series_id : ''));
          if (targetSeriesId) {
            try {
              const propsRes = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
              const props = Array.isArray(propsRes?.data) ? propsRes.data : [];
              for (const p of props) {
                const pid = String(p.id);
                try {
                  const serRes = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
                  const items = Array.isArray(serRes?.data) ? serRes.data : [];
                  if (items.some(s => String(s.id) === targetSeriesId)) {
                    setSelectedPropertyId(pid);
                    // Load structures for that property
                    try {
                      const cacheKey = `prop:${pid}`;
                      if (linkageOptionsCacheRef.current.has(cacheKey)) {
                        setInternalAssetOptions(linkageOptionsCacheRef.current.get(cacheKey));
                      } else {
                        const optRes = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
                        const root = optRes?.data || {};
                        const children = Array.isArray(root.children) ? root.children : [];
                        const options = [];
                        
                        if (isLandscaping) {
                          // For landscaping, we only want the Land asset in a unified list
                          const land = children.find(ch => String(ch.type).toLowerCase() === 'land');
                          if (land) {
                            options.push({ id: land.id, label: `${p.address} - Land`, propertyId: pid });
                          }
                        } else {
                          // Add entire property option
                          if (root.id) {
                            const plabel = root.label || (root.address ? `${root.address} - Property` : `Residential Property - ${pid}`);
                            options.push({ id: root.id, label: plabel });
                          }
                          // Add all children (structures and land)
                          for (const ch of children) {
                            if (!ch || !ch.id) continue;
                            const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
                            options.push({ id: ch.id, label: lbl });
                          }
                        }
                        linkageOptionsCacheRef.current.set(cacheKey, options);
                        setInternalAssetOptions(options);
                      }
                    } catch (e) {
                      setInternalAssetOptions([]);
                    }
                    // Ensure the linked asset is selected
                    const aid = String(initialValues?.asset_id || transaction?.linked_asset_id || '');
                    if (aid && String(selectedAssetId) !== aid) setSelectedAssetId(aid);
                    break;
                  }
                } catch (e) { /* continue */ }
              }
            } catch (e) { /* ignore */ }
          }
        }
        // Ensure the linked asset is selected
        if (String(selectedAssetId) !== aid) setSelectedAssetId(aid);
      } catch (e) {
        // ignore; user can still pick property manually
      }
    };
    hydrateFromLinkedAsset();
  }, [
    visible,
    busy,
    isResidential,
    isLandscaping,
    selectedPropertyId,
    selectedAssetId,
  ]);

  // Load series options when modal is visible.
  React.useEffect(() => {
    // If user already picked a series, don't keep reloading it every time other dependencies change
    if (userPickedSeries || userManuallySelectedSeriesRef.current) return;
    const run = async () => {
      try {
        if (!visible || busy || !isPhysicalAsset) return;
        const pid = (initialValuesRef.current?.property_id ? String(initialValuesRef.current.property_id) : '') || utilitiesConfigRef.current?.propertyId || selectedPropertyId || '';
        if (!pid && !isVehicle) { if (seriesOptions.length) setSeriesOptions([]); return; }
        
        const cacheKey = isVehicle ? `vehicle:${selectedAssetId || transactionRef.current?.linked_asset_id}` : `prop:${pid}`;
        if (seriesCacheRef.current.has(cacheKey)) {
          const items = seriesCacheRef.current.get(cacheKey);
          setSeriesOptions(items);
          return;
        }
        setSeriesLoading(true);
        // Include inactive to ensure historical series appear for editing
        const url = isVehicle 
          ? `/api/balance_sheet/assets/physical_assets/vehicles/${selectedAssetId || transactionRef.current?.linked_asset_id}/recurring-series/`
          : `/api/balance_sheet/assets/physical_assets/${pid}/recurring-series/`;
          
        if (url.includes('/undefined/') || url.includes('//recurring-series/')) return;
          
        const res = await axios.get(url, { params: { include_children: 1, include_inactive: 1 } });
        const items = Array.isArray(res.data) ? res.data : [];
        // Cache full list; we will filter by selected asset where needed to avoid race conditions
        seriesCacheRef.current.set(cacheKey, items);
        setSeriesOptions(items);
        setMetaLoading(false);
      } catch (e) {
        setSeriesOptions([]);
      } finally {
        setSeriesLoading(false);
      }
    };
    run();
  }, [
    visible,
    busy,
    isPhysicalAsset,
    isVehicle,
    selectedAssetId,
    selectedPropertyId,
  ]); // Removed seriesOptions.length

  React.useEffect(() => {
    if (!visible || busy || !isPhysicalAsset) return;
    try {
      const hasOptions = Array.isArray(seriesOptions) && seriesOptions.length > 0;
      if (!hasOptions) return;
      if (userPickedSeries || userManuallySelectedSeriesRef.current) return;
      // Determine desired series id directly from the transaction linkage first
      const desiredId = String(
        (transactionRef.current?.linked_recurring_series_id != null
          ? transactionRef.current.linked_recurring_series_id
          : (initialValuesRef.current?.recurring_series_id != null ? initialValuesRef.current.recurring_series_id : ''))
      );
      if (!desiredId) return;
      const list = getContextSeries();
      if (list.some(s => String(s.id) === desiredId)) {
        if (!busy) {
          setExistingSeriesId(desiredId);
          if (!userManuallyToggledRecurringRef.current) setRecurring(true);
          setRecurringMode('existing');
        }
        // Ensure Utilities/Media/Vehicle sub-category reflects the linked series category when not already provided
        const pick = list.find(s => String(s.id) === desiredId);
        if (pick && pick.expense_category && !busy) {
          if ((isUtilities || isMedia) && !utilCategory && (String(pick.expense_category).startsWith('utilities_') || String(pick.expense_category).startsWith('media_'))) {
            setUtilCategory(pick.expense_category);
          }
          if (isVehicle && !vehicleCategory) {
            setVehicleCategory(pick.expense_category);
          }
        }
        // Final lock after render to ensure the <select> reflects the chosen id
        requestAnimationFrame(() => {
          if (busy) return;
          try {
            const sel = document.getElementById('utilities-existing-series');
            if (sel && String(sel.value) !== String(desiredId)) sel.value = String(desiredId);
          } catch(e) {}
        });
      }
    } catch (e) { /* ignore */ }
  }, [
    visible,
    busy,
    isPhysicalAsset,
    isUtilities,
    isMedia,
    isVehicle,
    seriesOptionsMemo,
    selectedAssetId,
    selectedPropertyId,
    utilCategory,
    vehicleCategory,
    getContextSeries,
    userPickedSeries,
  ]); // Removed seriesOptions

  // Pre-load all Vehicles for the vehicle domain
  React.useEffect(() => {
    const loadVehicles = async () => {
      try {
        if (!visible || busy || !isVehicle) return;
        const res = await axios.get('/api/balance_sheet/assets/physical_assets/vehicles/');
        const list = Array.isArray(res.data) ? res.data : [];
        const options = list.map(v => ({ id: v.id, label: `${v.vehicle_year || ''} ${v.vehicle_make || ''} ${v.vehicle_model || ''}`.trim() || v.description || `Vehicle ${v.id}` }));
        setInternalAssetOptions(options);
        
        // Auto-select if there is only one vehicle
        if (options.length === 1 && !selectedAssetId && !busy) {
          setSelectedAssetId(String(options[0].id));
        }
      } catch (e) {
        setInternalAssetOptions([]);
      }
    };
    loadVehicles();
  }, [visible, busy, isVehicle, selectedAssetId]);

  // Load candidate utility bills for this transaction/property (Utilities/Media domains)
  React.useEffect(() => {
    // If user already picked a bill, don't keep reloading it every time other dependencies change
    if (userPickedBill || userManuallySelectedBillRef.current) return;
    const loadBills = async () => {
      try {
        if (!visible || busy || (!isUtilities && !isMedia)) return;
        let pid = (initialValuesRef.current?.property_id ? String(initialValuesRef.current.property_id) : '') || utilitiesConfigRef.current?.propertyId || selectedPropertyId || '';
        // If pid is missing or equals the selected structure id, try to infer property id from known series
        if ((!pid || (pid && selectedAssetId && String(pid) === String(selectedAssetId)))) {
          // Prefer seriesOptions (already loaded) to infer property id
          if (Array.isArray(seriesOptions) && seriesOptions.length > 0) {
            const any = seriesOptions.find(s => s && s.asset_id != null);
            if (any && any.asset_id != null) pid = String(any.asset_id);
          }
          // If still no pid, try linked series id directly
          if ((!pid || (pid && selectedAssetId && String(pid) === String(selectedAssetId)))) {
            const sid = String((initialValues?.recurring_series_id != null ? initialValues.recurring_series_id : (transaction?.linked_recurring_series_id != null ? transaction.linked_recurring_series_id : '')));
            if (sid) {
              try {
                const propsRes = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
                const props = Array.isArray(propsRes?.data) ? propsRes.data : [];
                for (const p of props) {
                  const pid2 = String(p.id);
                  try {
                    const serRes = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid2}/recurring-series/`, { params: { include_children: 1, include_inactive: 1 } });
                    const items = Array.isArray(serRes?.data) ? serRes.data : [];
                    if (items.some(s => String(s.id) === sid)) { pid = pid2; break; }
                  } catch (e) { /* continue */ }
                }
              } catch (e) { /* ignore */ }
            }
          }
        }
        if (!pid || !transaction?.id) { if (billOptions.length) setBillOptions([]); return; }
        setBillLoading(true); setBillError('');
        const base = sourceType === 'revolver'
          ? `/api/balance_sheet/financing/revolvers/transactions/${transaction.id}/candidate-utility-bills`
          : `/api/balance_sheet/assets/cash_accounts/transactions/${transaction.id}/candidate-utility-bills`;
        const serviceType = (utilCategory || '').trim();
        const cacheKey = `pid:${pid}|txn:${transaction.id}|svc:${serviceType}`;
        let arr;
        if (billsCacheRef.current.has(cacheKey)) {
          arr = billsCacheRef.current.get(cacheKey);
        } else {
          // Pass selected utilities sub-category as service_type so backend can filter providers/bills
          const url = `${base}?property_id=${encodeURIComponent(pid)}${serviceType?`&service_type=${encodeURIComponent(serviceType)}`:''}`;
          const res = await axios.get(url);
          arr = Array.isArray(res.data?.candidates) ? res.data.candidates : [];
          billsCacheRef.current.set(cacheKey, arr);
        }

        // Defensive client-side filter by provider allowlist as a backstop in case backend didn't filter
        const providerAllow = (() => {
          const st = String(serviceType).toLowerCase();
          if (!st) return null;
          if (st === 'utilities_water' || st === 'utilities_trash' || st === 'utilities_sewer') {
            return new Set(['city of ankeny', 'ankeny sanitation']);
          }
          if (st === 'utilities_electric_gas' || st === 'utilities_electric' || st === 'utilities_gas') {
            return new Set(['midamerican']);
          }
          if (st === 'utilities_internet' || st === 'utilities_cable_tv' || st === 'utilities_phone' || st === 'media_internet' || st === 'media_cable_tv' || st === 'media_phone') {
            return new Set(['mediacom','centurylink','verizon','at&t','comcast']);
          }
          if (st === 'media_streaming') {
            return new Set(['netflix', 'hulu', 'disney+', 'hbomax', 'apple tv+', 'paramount+', 'peacock', 'youtube tv', 'fubo tv', 'sling tv']);
          }
          if (st === 'media_books') {
            return new Set(['amazon', 'audible', 'kindle', 'barnes & noble', 'kobo', 'google play books', 'apple books']);
          }
          return null;
        })();
        if (providerAllow) {
          arr = arr.filter(b => {
            const p = String(b?.provider || '').toLowerCase();
            return providerAllow.has(p);
          });
        }

        // Preserve current selection if still present; otherwise apply heuristic preselect
        const prevSel = String(selectedBillId || '');
        // Compute desired preselection in priority order:
        const desired = (() => {
          // 1) explicit initial value
          if (initialValuesRef.current?.utility_bill_id != null) return String(initialValuesRef.current.utility_bill_id);
          // 2) linked on transaction
          if (transactionRef.current?.linked_utility_bill_id != null) return String(transactionRef.current.linked_utility_bill_id);
          // 3) keep previously chosen during this session if still valid
          if (selectedBillId) return selectedBillId;
          // 4) heuristic: current/linked, else single option
          const current = arr.find(b => b?.current === true || b?.is_current === true || b?.linked === true);
          if (current) return String(current.id);
          if (arr.length === 1) return String(arr[0].id);
          return '';
        })();
        setBillOptions(arr);
        // Force-select the linked bill id when present in candidates
        if (desired) {
          const match = arr.find(b => String(b.id) === desired);
          if (match) {
            setSelectedBillId(String(match.id));
          } else {
            // If desired not found, fall back to previous or heuristic
            if (prevSel && arr.some(b => String(b.id) === prevSel)) {
              setSelectedBillId(prevSel);
            } else {
              const current = arr.find(b => b?.current === true || b?.is_current === true || b?.linked === true);
              if (current) setSelectedBillId(String(current.id));
              else if (arr.length === 1) setSelectedBillId(String(arr[0].id));
              else setSelectedBillId('');
            }
          }
        } else {
          // nothing explicit requested; use heuristic
          const current = arr.find(b => b?.current === true || b?.is_current === true || b?.linked === true);
          if (current) setSelectedBillId(String(current.id));
          else if (arr.length === 1) setSelectedBillId(String(arr[0].id));
          else setSelectedBillId('');
        }
      } catch (e) {
        // Do not clear existing options on transient failure; show error and keep prior list/selection
        const status = e?.response?.status;
        setBillError(status ? `Failed to load utility bills (HTTP ${status}).` : 'Failed to load utility bills for this property.');
      } finally {
        setBillLoading(false);
      }
    };
    loadBills();
  }, [
    visible,
    isUtilities,
    isMedia,
    selectedPropertyId,
    sourceType,
    userPickedBill,
    utilCategory,
    selectedAssetId,
    busy,
    selectedBillId,
  ]); // Fixed dependencies

  // When Utilities category changes, clear any previous load error but keep the list until refresh completes
  React.useEffect(() => {
    if (!visible || busy || !isUtilities) return;
    setBillError('');
  }, [utilCategory, visible, isUtilities, busy]);

  // After bill options load, auto-select the linked bill if available and user hasn't picked one
  React.useEffect(() => {
    if (!visible || busy || !isUtilities) return;
    try {
      const hasOptions = Array.isArray(billOptions) && billOptions.length > 0;
      if (!hasOptions) return;
      if (userPickedBill || userManuallySelectedBillRef.current) return;
      // Determine desired bill id directly from the transaction linkage first
      const desiredId = String(
        (transactionRef.current?.linked_utility_bill_id != null
          ? transactionRef.current.linked_utility_bill_id
          : (initialValuesRef.current?.utility_bill_id != null ? initialValuesRef.current.utility_bill_id : ''))
      );
      if (!desiredId) return;
      if (billOptions.some(b => String(b.id) === desiredId)) {
        if (selectedBillId !== desiredId) setSelectedBillId(desiredId);
      } else {
        // Fallback: try to match by provider + period when ids differ in type/format
        try {
          const lk = billOptions.find(b => String(b.id) === desiredId);
          if (lk && selectedBillId !== String(lk.id)) setSelectedBillId(String(lk.id));
        } catch (e) { /* ignore */ }
      }
      // Final lock after render to ensure the <select> reflects the chosen id
      requestAnimationFrame(() => {
        try {
          const sel = document.getElementById('utility-bill-select');
          if (sel && String(sel.value) !== String(desiredId)) sel.value = String(desiredId);
        } catch(e) {}
      });
    } catch (e) { /* ignore */ }
  }, [
    visible,
    busy,
    isUtilities,
    billOptionsMemo,
    userPickedBill,
    selectedBillId,
  ]); // Added selectedBillId

  // Note: we rely on initialValues.utility_bill_id (from parent) or candidate list flags

  // When editing a linked txn, attempt to auto-preselect recurring settings from available series
  React.useEffect(() => {
    const preselect = async () => {
      if (!visible || busy || !isPhysicalAsset) return;
      if (userPickedSeries || userManuallySelectedSeriesRef.current) return; // do not override a user choice
      // If caller didn't mark as recurring, but there is a likely series match, enable it
      try {
        setMetaLoading(true);
        const sid = String(selectedAssetId || transactionRef.current?.linked_asset_id || '');
        if (!sid) { setMetaLoading(false); return; }
        // Ensure we have series options; if not, they will load via the other effect
        const list = getContextSeries();
        if (list.length === 0) { setMetaLoading(false); return; }
        // If parent provided an explicit recurring_series_id, prefer it
        const initialSeriesId = (initialValuesRef.current && initialValuesRef.current.recurring_series_id != null) ? String(initialValuesRef.current.recurring_series_id) : '';
        if (initialSeriesId) {
          const matchById = list.find(s => String(s.id) === initialSeriesId);
          if (matchById) {
            if (!userPickedSeries && !userManuallyToggledRecurringRef.current) {
              setRecurring(true);
              setRecurringMode('existing');
            }
            setExistingSeriesId(String(matchById.id));
            if (matchById.last_frequency) setFrequency(matchById.last_frequency);
            // Ensure Utilities/Media sub-category reflects the linked series category when not already provided
            if ((isUtilities || isMedia) && !utilCategory && matchById.expense_category && (String(matchById.expense_category).startsWith('utilities_') || String(matchById.expense_category).startsWith('media_'))) {
              setUtilCategory(matchById.expense_category);
            }
            if (isVehicle && !vehicleCategory && matchById.expense_category) {
              setVehicleCategory(matchById.expense_category);
            }
            setMetaLoading(false);
            return;
          }
        }
        // Check if the transaction is actually recurring or linked to a series
        const txnSeriesId = (transactionRef.current && transactionRef.current.linked_recurring_series_id != null) ? String(transactionRef.current.linked_recurring_series_id) : '';
        const initSeriesId = (initialValuesRef.current && initialValuesRef.current.recurring_series_id != null) ? String(initialValuesRef.current.recurring_series_id) : '';
        const txnRecurring = !!(transactionRef.current && transactionRef.current.linked_expense_info && transactionRef.current.linked_expense_info.recurring === true);
        const isExplicitlyRecurring = !!(txnSeriesId || initSeriesId || txnRecurring || initialValuesRef.current.recurring);

        if (!isExplicitlyRecurring) {
          setMetaLoading(false);
          return;
        }

        // Try to match by selected asset id and utilities/media sub-category
        const ec = String(transactionRef.current?.expense_category || initialValuesRef.current?.expense_category || '');
        // list already constrained to selected structure or property
        const byAsset = list;
        // Prefer exact category match when available
        let pick = null;
        const serCat = (v)=> String(v || '').toLowerCase();
        if ((isUtilities || isMedia) && ec && (ec.startsWith('utilities_') || ec.startsWith('media_'))) {
          // Some backends store just 'utilities' or 'media' as expense_category; accept that as a match too
          pick = byAsset.find(s => {
            const sc = serCat(s.expense_category);
            return sc === serCat(ec) || (isUtilities && sc === 'utilities') || (isMedia && sc === 'media');
          }) || null;
        } else if (isTaxes) {
          // For taxes, match any series that is categorized as taxes
          pick = byAsset.find(s => {
            const sc = serCat(s.expense_category);
            return sc.includes('tax');
          }) || null;
        } else if (isVehicle && ec) {
          pick = byAsset.find(s => serCat(s.expense_category) === serCat(ec)) || null;
        }
        // Fallback to first series for that asset
        if (!pick && byAsset.length > 0) {
          pick = byAsset[0];
        }
        if (pick) {
          // Double check one more time if we should actually be enabling recurring
          if (isExplicitlyRecurring) {
            if (!userManuallyToggledRecurringRef.current) setRecurring(true);
            setRecurringMode('existing');
            if (pick.last_frequency) setFrequency(pick.last_frequency);
            setExistingSeriesId(String(pick.id));
            // If util sub-category not set (e.g., editing from generic Edit Link), align it to the series category
            if ((isUtilities || isMedia) && !ec && pick.expense_category && (String(pick.expense_category).startsWith('utilities_') || String(pick.expense_category).startsWith('media_'))) {
              setUtilCategory(pick.expense_category);
            }
            if (isVehicle && !ec && pick.expense_category) {
              setVehicleCategory(pick.expense_category);
            }
            // Utilize series type for display in new-series panel default if user switches
            if (pick.type) setNewSeriesType(pick.type);
          }
        }
      } catch (e) {
        // ignore
      } finally {
        setMetaLoading(false);
      }
    };
    preselect();
  }, [
    visible,
    isPhysicalAsset,
    isUtilities,
    isMedia,
    isVehicle,
    isTaxes,
    selectedAssetId,
    utilCategory,
    vehicleCategory,
    seriesOptionsMemo,
    getContextSeries,
    userPickedSeries,
  ]); // Removed busy


  // When user chooses Existing and there are series for the selected structure, auto-pick a sensible default
  React.useEffect(() => {
    if (!visible || busy || !isPhysicalAsset) return;
    if (!recurring || recurringMode !== 'existing') return;
    if (existingSeriesId || userPickedSeries || userManuallySelectedSeriesRef.current) return; // already chosen
    if (!Array.isArray(seriesOptions) || seriesOptions.length === 0) return;
    // Prefer a series whose expense_category matches the selected utilities/media sub-category; otherwise pick first.
    const list = getContextSeries();
    if (list.length === 0) return;
    let pick = null;
    if ((isUtilities || isMedia) && utilCategory) {
      // Accept both exact utilities/media_* match and generic 'utilities' or 'media'
      const target = String(utilCategory).toLowerCase();
      pick = list.find(s => {
        const sc = String(s.expense_category || '').toLowerCase();
        return sc === target || (isUtilities && sc === 'utilities') || (isMedia && sc === 'media');
      }) || null;
    } else if (isTaxes) {
      // For taxes, prefer a series that has 'tax' in its category
      pick = list.find(s => String(s.expense_category || '').toLowerCase().includes('tax')) || null;
    }
    if (!pick) pick = list[0] || null;
    if (pick) {
      const pidStr = String(pick.id);
      if (existingSeriesId !== pidStr) {
        setExistingSeriesId(pidStr);
      }
      // Only auto-enable recurring if it wasn't already user-interacted
      if (!userPickedSeries && !userManuallyToggledRecurringRef.current) {
        if (!recurring) setRecurring(true);
      }
    }
  }, [
    visible,
    busy,
    isPhysicalAsset,
    isUtilities,
    isMedia,
    isTaxes,
    isHome,
    isLandscaping,
    isVehicle,
    recurring,
    recurringMode,
    seriesOptionsMemo,
    utilCategory,
    selectedAssetId,
    getContextSeries,
    userPickedSeries,
    vehicleCategory,
  ]); // Removed existingSeriesId and seriesOptions

  if (!visible) return null;

  const isEdit = !!transaction?.linked_asset_id;

  const getRealId = (tid) => {
    if (typeof tid === 'string' && tid.startsWith('synth-')) {
      const parts = tid.split('-');
      if (parts.length >= 3) return parts[parts.length - 1];
    }
    return tid;
  };

  const linkUrl = () => {
    const tid = getRealId(transaction.id);
    return sourceType === 'revolver'
      ? `/api/balance_sheet/financing/revolvers/transactions/${tid}/link-asset-expense/`
      : `/api/balance_sheet/assets/cash_accounts/transactions/${tid}/link-asset-expense/`;
  };

  const unlinkUrl = () => {
    const tid = getRealId(transaction.id);
    return sourceType === 'revolver'
      ? `/api/balance_sheet/financing/revolvers/transactions/${tid}/unlink-asset-expense/`
      : `/api/balance_sheet/assets/cash_accounts/transactions/${tid}/unlink-asset-expense/`;
  };

  const handleSave = async () => {
    if (!selectedAssetId && !effectivelyUnlinkedAsset) {
      setError('Please select an asset.');
      return;
    }
    if (isUtilities || isMedia || isTaxes || isHome || isLandscaping) {
      // Require a sub-category if not unlinking everything
      if (!effectivelyUnlinkedAsset && (isUtilities || isMedia) && !utilCategory) {
        setError(`Please select a ${isMedia ? 'media' : 'utilities'} sub-category.`);
        return;
      }
      // Validate recurring series inputs if applicable
      if (recurring && !effectivelyUnlinkedSeries) {
        if (recurringMode === 'existing') {
          if (!existingSeriesId) { setError('Select an existing recurring series.'); return; }
        } else {
          const nm = (newSeriesName || '').trim();
          if (!nm) { setError('Enter a name for the new recurring series.'); return; }
          // For new series, we also want to ensure we have a frequency and type (they have defaults but let's be safe)
          if (!frequency) { setError('Please select a frequency.'); return; }
        }
      }
    }
    setBusy(true);
    setError('');
    try {
      // If the user unlinked the asset, we call the unlink endpoint instead of link
      if (effectivelyUnlinkedAsset) {
        await axios.post(`${unlinkUrl()}`);
        if (typeof onUnlinked === 'function') {
          await onUnlinked();
        }
        onClose && onClose();
        return;
      }

      const body = {
        asset_id: Number(selectedAssetId),
        expense_category: (isUtilities || isMedia) ? (utilCategory || (isMedia ? 'media_other' : 'utilities_other')) : (isVehicle ? (vehicleCategory || 'maintenance') : (isTaxes ? 'taxes' : (transaction?.expense_category || initialValues?.expense_category || 'none'))),
        recurring: !!recurring && !effectivelyUnlinkedSeries,
        recurring_frequency: (recurring && !effectivelyUnlinkedSeries) ? frequency : null,
        description: transaction?.description,
      };
      if (selectedTodoId) {
        body.todo_id = Number(selectedTodoId);
      }
      if ((isUtilities || isMedia) && selectedBillId && !effectivelyUnlinkedBill) {
        body.utility_bill_id = Number(selectedBillId);
      }
      if (recurring && !effectivelyUnlinkedSeries) {
        if (recurringMode === 'existing') {
          if (existingSeriesId) body.recurring_series_id = Number(existingSeriesId);
          // Keep name as best-effort for backward compatibility
          const selById = (seriesOptions||[]).find(s=> String(s.id)===String(existingSeriesId));
          if (selById && selById.name) body.recurring_name = selById.name;
          } else {
          body.recurring_name = newSeriesName;
          body.recurring_type = newSeriesType || 'variable';
          body.recurring_frequency = frequency || 'monthly';
          }
      }
      // Special case: if user unlinked series or bill while keeping asset link, we might need a way to clear them.
      // The current backend link-asset-expense logic usually overwrites.
      if (effectivelyUnlinkedBill) body.utility_bill_id = null;
      if (effectivelyUnlinkedSeries) {
          body.recurring = false;
          body.recurring_series_id = null;
          body.recurring_name = null;
      }

      const res = await axios.post(`${linkUrl()}`, body);
      const ae = (res && res.data && res.data.asset_expense) ? res.data.asset_expense : null;
      if (typeof onSaved === 'function') {
        await onSaved({
          asset_id: Number(selectedBillId ? selectedAssetId : selectedAssetId),
          recurring: !!recurring,
          recurring_frequency: recurring ? frequency : null,
          utility_bill_id: selectedBillId ? Number(selectedBillId) : (ae && ae.utility_bill_id != null ? ae.utility_bill_id : null),
          asset_expense_id: ae && ae.id != null ? ae.id : null,
          recurring_series_id: ae && ae.recurring_series_id != null ? ae.recurring_series_id : null,
          recurring_type: ae && ae.recurring_type ? ae.recurring_type : null,
        });
      }
      onClose && onClose();
    } catch (e) {
      setError('Failed to save link.');
    } finally {
      setBusy(false);
    }
  };


  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }} onClick={()=> !busy && onClose && onClose()}>
      <div style={{ width:'min(680px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:18, fontWeight:700 }}>
            {isMedia ? "Media Expense - Linkages" : (isUtilities ? "Utility Expense - Linkages" : (isVehicle ? "Automotive Expense - Linkages" : (isEdit ? `Edit Link — ${contextLabel}` : `Link ${contextLabel}`)))}
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={busy}>✕</button>
          </div>
        </div>
        <div style={{ padding:16, maxHeight:'85vh', overflowY:'auto' }}>
          {error && (
            <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{error}</div>
          )}
          {metaLoading && (
            <div className="text-muted" style={{ color:'#9ca3af', marginBottom:8 }}>Loading existing recurring info…</div>
          )}

          {/* Transaction summary: Date — Description: $Amount */}
          {transaction && (
            <div style={{ marginBottom:16, padding:10, border:'1px solid #374151', borderRadius:6, background:'#111827', color:'#e5e7eb' }}>
              {(() => {
                const iso = transaction.transaction_date || transaction.post_date || transaction.date || '';
                const desc = transaction.description || '';
                const amtNum = (()=>{ try { return Math.abs(Number(transaction.amount||0)); } catch(e){ return 0; } })();
                const amt = amtNum.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
                return (
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ color:'#cbd5e1' }}>{iso}</span>
                    <span>—</span>
                    <span style={{ flex:1 }}>{desc}</span>
                    <span style={{ fontWeight:600 }}>{amt}</span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Utilities/Media Sub-Category (Move to top per request) */}
          {(isUtilities || isMedia) && !effectivelyUnlinkedAsset && (
            <div style={{ marginBottom: 20 }}>
              {(isEdit && utilCategory && !isEditingUtilCategory) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#111827', padding: 10, borderRadius: 6, border: '1px solid #374151' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>{isMedia ? 'Media Sub-Category' : 'Utilities Sub-Category'}</div>
                    <div style={{ fontWeight: 600 }}>
                      {(utilitiesConfig?.categories || []).find(c => String(c.value) === String(utilCategory))?.label || utilCategory}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    style={{ minWidth: 70 }}
                    onClick={() => {
                      const msg = `Changing the ${isMedia ? 'Media' : 'Utilities'} Sub-Category will automatically unlink the current Recurring Series and Utility Bill for this transaction. Continue?`;
                      if (window.confirm(msg)) {
                        setIsEditingUtilCategory(true);
                        // Cascading unlinks
                        setEffectivelyUnlinkedSeries(true);
                        setRecurring(false);
                        setExistingSeriesId('');
                        setEffectivelyUnlinkedBill(true);
                        setSelectedBillId('');
                      }
                    }}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div style={{ background: '#111827', padding: 10, borderRadius: 6, border: '1px solid #374151' }}>
                  <label className="form-label" htmlFor="utilities-sub-category-select" style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4, display: 'block' }}>{isMedia ? 'Media Sub-Category' : 'Utilities Sub-Category'}</label>
                  <select
                    id="utilities-sub-category-select"
                    name="utilities-sub-category-select"
                    className="form-select"
                    value={utilCategory}
                    onChange={(e) => {
                      setUtilCategory(e.target.value);
                      // If we were editing from an existing link, ensure cascading unlinks are active
                      if (isEdit) {
                        setEffectivelyUnlinkedSeries(true);
                        setRecurring(false);
                        setExistingSeriesId('');
                        setEffectivelyUnlinkedBill(true);
                        setSelectedBillId('');
                      }
                    }}
                  >
                    {(isMedia ? (mediaConfig?.categories || []) : (utilitiesConfig?.categories || []))
                      .filter(c => c && c.value != null && c.value !== '')
                      .map(c => (
                        <option key={`${isMedia ? 'media' : 'util'}cat-${String(c.value)}`} value={c.value}>{c.label}</option>
                      ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Vehicle Sub-Category */}
          {isVehicle && !effectivelyUnlinkedAsset && (
            <div style={{ marginBottom: 20 }}>
              {(isEdit && vehicleCategory && !isEditingVehicleCategory) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#111827', padding: 10, borderRadius: 6, border: '1px solid #374151' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>Automotive Expense Category</div>
                    <div style={{ fontWeight: 600 }}>
                      {prettyFreq(vehicleCategory)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    style={{ minWidth: 70 }}
                    onClick={() => {
                      const msg = "Changing the category will automatically unlink the current Recurring Series for this transaction. Continue?";
                      if (window.confirm(msg)) {
                        setIsEditingVehicleCategory(true);
                        setEffectivelyUnlinkedSeries(true);
                        setRecurring(false);
                        setExistingSeriesId('');
                      }
                    }}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div style={{ background: '#111827', padding: 10, borderRadius: 6, border: '1px solid #374151' }}>
                  <label className="form-label" htmlFor="vehicle-category-select" style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4, display: 'block' }}>Automotive Expense Category</label>
                  <select
                    id="vehicle-category-select"
                    name="vehicle-category-select"
                    className="form-select"
                    value={vehicleCategory}
                    onChange={(e) => {
                      setVehicleCategory(e.target.value);
                      if (isEdit) {
                        setEffectivelyUnlinkedSeries(true);
                        setRecurring(false);
                        setExistingSeriesId('');
                      }
                    }}
                  >
                    <option value="asset_input">Asset Input (Fuel, Charging)</option>
                    <option value="maintenance">Maintenance (Oil, Tires)</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="repair">Repair</option>
                    <option value="enhancement">Enhancement (Upgrades)</option>
                    <option value="asset_storage">Asset Storage (Parking)</option>
                    <option value="taxes">Tax Payment (Registration)</option>
                    <option value="licensing_tags">Licensing/Tags</option>
                    <option value="insurance">Insurance Payment</option>
                    <option value="ticket_or_fine">Ticket or Fine</option>
                    <option value="financing_interest">Financing Interest</option>
                    <option value="financing_principal">Financing Principal</option>
                    <option value="initial_purchase">Initial Purchase</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Section 1: Property / Asset */}
          <div style={{ marginBottom: 20, borderBottom: '1px solid #1f2937', pb: 12 }}>
            
            {(isEdit && !effectivelyUnlinkedAsset) ? (
              <div style={{ display:'flex', alignItems:'center', gap:12, background:'#111827', padding:10, borderRadius:6, border:'1px solid #374151' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:'#9ca3af', marginBottom:2 }}>
                    {isResidential ? (isLandscaping ? 'Linked Property & Land' : 'Residential Property & Structure') : (isVehicle ? 'Linked Vehicle' : 'Linked Asset')}
                  </div>
                  <div style={{ fontWeight:600 }}>
                    {(() => {
                      const aid = selectedAssetId || transaction?.linked_asset_id;
                      // Prefer the explicitly loaded detail if available
                      const opt = selectedAssetDetail || (assetOptions.length ? assetOptions : internalAssetOptions).find(o => String(o.id) === String(aid));
                      
                      if (opt) {
                        // Use explicit label if it's not generic
                        if (opt.label && !opt.label.startsWith('Asset ') && !opt.label.startsWith('Structure ') && !opt.label.startsWith('Vehicle ') && !opt.label.startsWith('Physical Asset ') && !opt.label.startsWith('Property ') && !opt.label.startsWith('Residential Property ')) {
                          return opt.label;
                        }

                        const t2 = String(opt.type || opt.asset_type_2 || '').toLowerCase();
                        if (t2 === 'vehicle' || isVehicle) {
                          const parts = [];
                          if (opt.vehicle_year || opt.year) parts.push(opt.vehicle_year || opt.year);
                          if (opt.vehicle_make || opt.make) parts.push(opt.vehicle_make || opt.make);
                          if (opt.vehicle_model || opt.model) parts.push(opt.vehicle_model || opt.model);
                          if (parts.length > 0) return parts.join(' ');
                          return opt.label || opt.description || `Vehicle ${opt.id || aid}`;
                        }
                        
                        // Property related logic
                        const pId = selectedPropertyId || transaction?.linked_property_id || opt.propertyId || (opt.parent && opt.parent.id);
                        const pOpt = propertyOptions.find(p => String(p.id) === String(pId)) || (opt.parent && String(opt.parent.type).toLowerCase().includes('property') ? opt.parent : null);
                        const street = pOpt ? (pOpt.address || pOpt.label) : (opt.address || '');

                        if (isLandscaping || t2 === 'land') {
                          if (street && !street.toLowerCase().includes('land')) return `${street} - Land`;
                          let l = opt.label || (t2 === 'land' ? 'Land' : '');
                          if (l && !l.toLowerCase().includes(' - land') && !l.toLowerCase().endsWith(' land')) {
                            l = `${l} - Land`;
                          }
                          return l || 'Land';
                        }
                        
                        if (t2 === 'structure' || (t2 === 'physical_asset' && opt.asset_type_1 !== 'resi_property')) {
                          const rawSubType = opt.asset_type_1 || opt.type_1 || opt.type1 || 'Structure';
                          const subType = (rawSubType === 'Structure' || rawSubType === 'physical_asset') ? 'Structure' : rawSubType.replace(/ORM$/, '');
                          
                          if (street) {
                            // Resolve ordinal if needed, but if backend provided a label use it as base for suffix if needed
                            const structs = (assetOptions.length ? assetOptions : internalAssetOptions).filter(o => String(o.type || o.asset_type_2 || '').toLowerCase() === 'structure').sort((a,b)=>a.id-b.id);
                            const sIdx = structs.findIndex(o => String(o.id) === String(aid));
                            const ordinal = sIdx !== -1 ? sIdx + 1 : '';
                            const ordinalPart = (ordinal && String(ordinal) !== '1') ? ' ' + ordinal : '';
                            return `${street} - ${subType}${ordinalPart}`;
                          }
                          return opt.label || `${subType} ${aid}`;
                        }
                        
                        if (t2 === 'resi_property' || t2 === 'property' || (opt.asset_type_1 === 'resi_property')) {
                           if (street && !street.toLowerCase().includes('property') && !street.toLowerCase().includes('house')) return `${street} - Property`;
                           return street || opt.label || `Property ${opt.id || aid}`;
                        }

                        return opt.label || street || `Asset ${aid}`;
                      }
                      
                      if (assetDetailLoading) return 'Loading asset detail...';
                      return (aid ? `Asset ${aid}` : 'Unknown Asset');
                    })()}
                  </div>
                  {isResidential && selectedPropertyId && !isLandscaping && (
                    <div style={{ fontSize:12, color:'#9ca3af' }}>
                      {propertyOptions.find(p => String(p.id) === String(selectedPropertyId))?.address}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ minWidth: 70 }}
                  onClick={() => {
                    const msg = (isUtilities || isMedia)
                      ? `Unlinking this property will also automatically unlink any associated ${isMedia ? 'Media' : 'Utility'} Bill. Are you sure?`
                      : "Are you sure you want to unlink this asset?";
                    if (window.confirm(msg)) {
                      setEffectivelyUnlinkedAsset(true);
                      if (isUtilities || isMedia) {
                        setEffectivelyUnlinkedBill(true);
                        setSelectedBillId('');
                      }
                      setSelectedAssetId('');
                      if (!isLandscaping) setSelectedPropertyId('');
                    }
                  }}
                >
                  Unlink
                </button>
              </div>
            ) : (
              <>
                {/* Residential domain: Property picker (for pages where propertyId is not provided) */}
                {isResidential && !utilitiesConfig?.propertyId && (
                  <div style={{ marginBottom:12 }}>
                    <label className="form-label" htmlFor="resi-property-select">Residential Property</label>
                    <select
                      id="resi-property-select"
                      name="resi-property-select"
                      className="form-select"
                      value={selectedPropertyId}
                      onChange={async (e)=>{
                        const pid = e.target.value;
                        setSelectedPropertyId(pid);
                        // Mark as manually changed so the pre-population effect doesn't clear it
                        userManuallySelectedPropertyRef.current = true;
                        try {
                          if (pid) {
                            const resp = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
                            const root = resp?.data || {};
                            const children = Array.isArray(root.children) ? root.children : [];
                            const options = [];
                            if (isLandscaping) {
                              // For landscaping, we only want the Land asset
                              const land = children.find(ch => String(ch.type).toLowerCase() === 'land');
                              if (land) {
                                options.push({ id: land.id, label: `${root.address || 'Property'} - Land`, propertyId: pid });
                              }
                            } else {
                              // Add entire property option
                              if (root.id) {
                                const plabel = root.label || (root.address ? `${root.address} - Property` : `Residential Property - ${pid}`);
                                options.push({ id: root.id, label: plabel });
                              }
                              // Add all children (structures and land)
                              for (const ch of children) {
                                if (!ch || !ch.id) continue;
                                const lbl = ch.label || (ch.type ? `${String(ch.type).charAt(0).toUpperCase() + String(ch.type).slice(1)} ${ch.id}` : `Asset ${ch.id}`);
                                options.push({ id: ch.id, label: lbl });
                              }
                            }
                            setInternalAssetOptions(options);
                            if (!selectedAssetId && options.length) {
                              // Auto-select property if it's the only one, or if it's taxes
                              if (options.length === 1 || isTaxes) {
                                setSelectedAssetId(String(options[0].id));
                              }
                            }
                          } else {
                            setInternalAssetOptions([]);
                            setSelectedAssetId('');
                          }
                        } catch (err) { setInternalAssetOptions([]); }
                      }}
                      onFocus={async ()=>{
                        if (propertyOptions.length) return;
                        try {
                          const res = await axios.get('/api/balance_sheet/assets/physical_assets/property-choices/');
                          setPropertyOptions(Array.isArray(res.data) ? res.data : []);
                        } catch (e) { setPropertyOptions([]); }
                      }}
                    >
                      <option value="">Select property…</option>
                      {propertyOptions.map(p => (
                        <option key={p.id} value={String(p.id)}>{p.address?.street_address || p.label || p.address || `Property ${p.id}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ marginBottom:8 }}>
                  {isLandscaping ? 'Choose a Property - Land:' : (isVehicle ? 'Choose a Vehicle:' : (isResidential ? 'Choose a structure or land:' : 'Choose an asset:'))}
                </div>
                <div style={{ maxHeight: '30vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                  <table style={{ width:'100%' }}>
                    <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                      <tr>
                        <th style={{ textAlign:'left', padding:8 }}>Label</th>
                        <th style={{ textAlign:'right', padding:8 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(assetOptions.length ? assetOptions : internalAssetOptions).map((opt, idx) => {
                        const isLinked = String(selectedAssetId) === String(opt.id);
                        const t2 = String(opt.type || opt.asset_type_2 || '').toLowerCase();
                        let label = opt.label;
                      if (isLandscaping) {
                        const pId = selectedPropertyId || transaction?.linked_property_id || opt.propertyId;
                        const pOpt = propertyOptions.find(p => String(p.id) === String(pId));
                        const street = pOpt ? (pOpt.address?.street_address || pOpt.label || pOpt.address) : '';
                        if (street) {
                          label = `${street} - Land`;
                        } else if (!label.toLowerCase().includes(' - land') && !label.toLowerCase().endsWith(' land')) {
                          label = `${label} - Land`;
                        }
                      } else if (t2 === 'structure') {
                           const pId = selectedPropertyId || transaction?.linked_property_id || opt.propertyId;
                           const structs = (assetOptions.length ? assetOptions : internalAssetOptions).filter(o => String(o.type || o.asset_type_2 || '').toLowerCase() === 'structure').sort((a,b)=>a.id-b.id);
                           const sIdx = structs.findIndex(o => String(o.id) === String(opt.id));
                           if (sIdx !== -1) {
                              const pOpt = propertyOptions.find(p => String(p.id) === String(pId));
                              const street = pOpt ? (pOpt.address?.street_address || pOpt.label || pOpt.address) : '';
                              const rawSubType = opt.asset_type_1 || opt.type_1 || opt.type1 || 'Structure';
                              const subType = (rawSubType === 'Structure' || rawSubType === 'physical_asset') ? 'Structure' : rawSubType.replace(/ORM$/, '');
                              const ordinalPart = (sIdx !== 0) ? ` ${sIdx + 1}` : '';
                              label = `${street}${street ? ' - ' : ''}${subType}${ordinalPart}`;
                           }
                        }
                        return (
                          <tr key={opt.id} style={{ background: idx%2? '#0f172a':'#111827' }}>
                            <td style={{ padding:8 }}>{label}</td>
                            <td style={{ padding:8, textAlign:'right' }}>
                              <button
                                type="button"
                                className={`btn btn-sm ${isLinked ? 'btn-warning' : 'btn-primary'}`}
                                onClick={() => {
                                  if (isLinked) {
                                    setSelectedAssetId('');
                                    if (isLandscaping) setSelectedPropertyId('');
                                  } else {
                                    setSelectedAssetId(String(opt.id));
                                    setEffectivelyUnlinkedAsset(false);
                                    if (isLandscaping && opt.propertyId) {
                                      setSelectedPropertyId(String(opt.propertyId));
                                    }
                                  }
                                }}
                                disabled={busy}
                              >
                                {isLinked ? 'Unlink' : 'Link'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {(assetOptions.length === 0 && internalAssetOptions.length === 0 && !selectedAssetId) && (
                        <tr>
                          <td colSpan={2} style={{ padding:12, color:'#9ca3af' }}>
                            {isResidential && !selectedPropertyId
                              ? (isLandscaping ? 'Please select a residential property above to see available Land assets.' : 'Please select a residential property above to see available assets.')
                              : 'No selectable assets available.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Section 2: To-Do Item */}
          {isPhysicalAsset && (selectedAssetId || transaction?.linked_asset_id || initialValues?.asset_id) && (
            <div style={{ marginBottom: 20, borderBottom: '1px solid #1f2937', paddingBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb', marginBottom: 8 }}>Link to To-Do Item (Optional)</div>
              {assetDetailLoading ? (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading To-Do items...</div>
              ) : todoOptions.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <select
                    className="form-select"
                    value={selectedTodoId}
                    onChange={(e) => setSelectedTodoId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">-- Select To-Do Item --</option>
                    {todoOptions.map(t => (
                      <option key={`todo-${t.id}`} value={t.id}>{t.label || t.title || `To-Do #${t.id}`}</option>
                    ))}
                  </select>
                  {selectedTodoId && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setSelectedTodoId('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: 13, fontStyle: 'italic' }}>No To-Do items found for this property.</div>
              )}
            </div>
          )}

          {/* Section 2: Recurring Series */}
          <div style={{ marginBottom:20, borderBottom:'1px solid #1f2937', pb:12 }}>

            {(isEdit && (transaction?.linked_recurring_series_id || initialValues?.recurring_series_id) && !effectivelyUnlinkedSeries) ? (
              <div style={{ display:'flex', alignItems:'center', gap:12, background:'#111827', padding:10, borderRadius:6, border:'1px solid #374151' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:'#9ca3af', marginBottom:2 }}>Recurring Series</div>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {(() => {
                      const sid = existingSeriesId || transaction?.linked_recurring_series_id || initialValues?.recurring_series_id;
                      const s = seriesOptions.find(x => String(x.id) === String(sid));
                      return s ? s.name : (sid ? `Series ${sid}` : 'Linked Series');
                    })()}
                    {(() => {
                      const sid = existingSeriesId || transaction?.linked_recurring_series_id || initialValues?.recurring_series_id;
                      const s = seriesOptions.find(x => String(x.id) === String(sid));
                      if (!s) return null;
                      return (
                        <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
                          • {prettyFreq(s.last_frequency)} • {prettyType(s.type)}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ minWidth: 70 }}
                  onClick={() => {
                    if (window.confirm("Are you sure you want to unlink this transaction from the recurring series?")) {
                      setEffectivelyUnlinkedSeries(true);
                      setRecurring(false);
                      setExistingSeriesId('');
                    }
                  }}
                >
                  Unlink
                </button>
              </div>
            ) : (
              <>
                <div className="form-check" style={{ marginTop:8 }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="recurringCheckShared"
                    name="recurringCheckShared"
                    checked={recurring}
                    onChange={(e)=> { 
                      setRecurring(e.target.checked); 
                      userManuallyToggledRecurringRef.current = true;
                      if(e.target.checked) setEffectivelyUnlinkedSeries(false); 
                    }}
                  />
                  <label className="form-check-label" htmlFor="recurringCheckShared" style={{ marginLeft:6 }}>Recurring</label>
                </div>

                {recurring && (
                  <div style={{ marginTop:8, border:'1px solid #374151', borderRadius:6, padding:10 }}>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                      <button type="button" className={`btn ${recurringMode==='existing'?'btn-primary':'btn-outline-primary'}`} onClick={()=> setRecurringMode('existing')}>Existing</button>
                      <button type="button" className={`btn ${recurringMode==='new'?'btn-primary':'btn-outline-primary'}`} onClick={()=> setRecurringMode('new')}>New</button>
                    </div>

                    {recurringMode === 'existing' && (
                      <div style={{ marginTop:8 }}>
                        <label className="form-label" htmlFor="utilities-existing-series">Existing recurring series</label>
                        <select
                          id="utilities-existing-series"
                          name="utilities-existing-series"
                          className="form-select"
                          value={existingSeriesId}
                          onChange={(e)=> { 
                            const val = e.target.value;
                            setExistingSeriesId(val); 
                            setUserPickedSeries(true); 
                            userManuallySelectedSeriesRef.current = true;
                            setEffectivelyUnlinkedSeries(false); 
                            
                            // Clear bill when series changes unless we just picked the same series
                            // and the bill is already compatible (actually safer to just clear it
                            // so the user re-selects or the auto-preselect runs for the new series)
                            if (!userManuallySelectedBillRef.current) {
                               setSelectedBillId('');
                               setUserPickedBill(false);
                            }
                            
                            // If user picked a series, try to auto-set the category if it matches the series
                            const list = getContextSeries();
                            const pick = list.find(s => String(s.id) === String(val));
                            if (pick && pick.expense_category) {
                              if ((isUtilities || isMedia) && (String(pick.expense_category).startsWith('utilities_') || String(pick.expense_category).startsWith('media_'))) {
                                setUtilCategory(pick.expense_category);
                              }
                            }
                          }}
                          disabled={(!selectedAssetId && !utilitiesConfig?.propertyId) || seriesLoading}
                        >
                          <option value="">{seriesLoading ? 'Loading…' : '-- Select series --'}</option>
                          {getContextSeries().map(s => {
                            const parts = [s.name];
                            if (s.last_frequency) parts.push(prettyFreq(s.last_frequency));
                            if (s.type) parts.push(prettyType(s.type));
                            return (
                              <option key={`series-${String(s.id)}`} value={String(s.id)}>
                                {parts.join(' - ')}
                              </option>
                            );
                          })}
                        </select>
                        {(!seriesLoading && getContextSeries().length === 0) && <small className="form-text text-muted">No existing recurring series found for this asset.</small>}
                      </div>
                    )}

                    {recurringMode === 'new' && (
                      <div style={{ marginTop:8 }}>
                        <div className="mb-2">
                          <label className="form-label" htmlFor="new-series-name">New series name</label>
                          <input id="new-series-name" className="form-control" type="text" value={newSeriesName} onChange={(e)=> setNewSeriesName(e.target.value)} placeholder="e.g., Water" />
                        </div>
                        <div className="mb-2">
                          <label className="form-label" htmlFor="new-series-frequency">Frequency</label>
                          <select id="new-series-frequency" className="form-select" value={frequency} onChange={(e)=> setFrequency(e.target.value)}>
                            {['weekly','monthly','bi_monthly','quarterly','semi_annually','annually'].map(f => (
                              <option key={f} value={f}>{prettyFreq(f)}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="form-label" htmlFor="new-series-type">Series type</label>
                          <select id="new-series-type" className="form-select" value={newSeriesType} onChange={(e)=> setNewSeriesType(e.target.value)}>
                            <option value="fixed">Fixed</option>
                            <option value="variable">Variable</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Remove: (isHome || isLandscaping || isVehicle) && !recurring frequency block as per requirement */}
              </>
            )}
          </div>

          {/* Section 3: Utility Bill (Utilities Only) */}
          {isUtilities && (
            <div style={{ marginBottom:20 }}>

              {(isEdit && (transaction?.linked_utility_bill_id || initialValues?.utility_bill_id) && !effectivelyUnlinkedBill) ? (
                <div style={{ display:'flex', alignItems:'center', gap:12, background:'#111827', padding:10, borderRadius:6, border:'1px solid #374151' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, color:'#9ca3af', marginBottom:2 }}>Utility Bill</div>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(() => {
                        const bid = selectedBillId || transaction?.linked_utility_bill_id || initialValues?.utility_bill_id;
                        const b = billOptions.find(x => String(x.id) === String(bid));
                        if (b) return b.provider || 'Linked Bill';
                        return bid ? `Bill ${bid}` : 'Linked Bill';
                      })()}
                      {(() => {
                        const bid = selectedBillId || transaction?.linked_utility_bill_id || initialValues?.utility_bill_id;
                        const b = billOptions.find(x => String(x.id) === String(bid));
                        if (!b) return null;
                        const pStart = b.billing_period_start || b.period_start || b.service_start;
                        const pEnd = b.billing_period_end || b.period_end || b.service_end;
                        const amt = b.total_amount || b.amount;
                        const txAmt = Math.abs(Number(transaction?.amount || 0));
                        const billAmt = Math.abs(Number(amt || 0));
                        const diff = Math.abs(txAmt - billAmt);
                        const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        const diffColor = diff > 0.009 ? '#f87171' : '#34d399';
                        return (
                          <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
                            • {pStart} → {pEnd} • {fmt(billAmt)}
                            <span style={{ margin: '0 6px' }}>•</span>
                            <span style={{ color: diffColor }}>Diff: {fmt(diff)}</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    style={{ minWidth: 70 }}
                    onClick={() => {
                      if (window.confirm("Are you sure you want to unlink this utility bill?")) {
                        setEffectivelyUnlinkedBill(true);
                        setSelectedBillId('');
                      }
                    }}
                  >
                    Unlink
                  </button>
                </div>
              ) : (
                <>
                  <label className="form-label" htmlFor="utility-bill-select">Utility Bill (optional)</label>
                  <select
                    id="utility-bill-select"
                    name="utility-bill-select"
                    className="form-select"
                    value={selectedBillId}
                    onChange={(e)=> { 
                      setSelectedBillId(e.target.value); 
                      setUserPickedBill(true); 
                      userManuallySelectedBillRef.current = true;
                      setEffectivelyUnlinkedBill(false); 
                    }}
                    disabled={billLoading || effectivelyUnlinkedAsset}
                  >
                    <option key="__bill_placeholder__" value="">{billLoading ? 'Loading…' : '-- Select a bill --'}</option>
                    {billOptions.filter(b => b && b.id != null).map((b) => {
                      const labelParts = [];
                      if (b.provider) labelParts.push(b.provider);
                      const pStart = b.billing_period_start || b.period_start || b.service_start;
                      const pEnd = b.billing_period_end || b.period_end || b.service_end;
                      if (pStart || pEnd) {
                        labelParts.push(`${pStart || ''} → ${pEnd || ''}`);
                      } else if (b.statement_period_year && b.statement_period_month) {
                        const monthName = new Date(b.statement_period_year, b.statement_period_month - 1).toLocaleString('default', { month: 'short' });
                        labelParts.push(`${monthName} ${b.statement_period_year}`);
                      }
                      const amtVal = b.total_amount || b.amount;
                      if (amtVal != null) labelParts.push(`$${Number(amtVal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                      return (
                        <option key={`bill-${String(b.id)}`} value={String(b.id)}>
                          {labelParts.join(' • ')}
                        </option>
                      );
                    })}
                  </select>
                  {billError && <div className="form-text" style={{ color:'#f87171' }}>{billError}</div>}
                  {selectedBillId && !effectivelyUnlinkedBill && (
                    <div style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(() => {
                        const b = billOptions.find(x => String(x.id) === String(selectedBillId));
                        if (!b) return null;
                        const txAmt = Math.abs(Number(transaction?.amount || 0));
                        const billAmt = Math.abs(Number(b.total_amount ?? b.amount ?? 0));
                        const diff = Math.abs(txAmt - billAmt);
                        const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        const color = diff > 0.009 ? '#f87171' : '#34d399';
                        const pStart = b.billing_period_start || b.period_start || b.service_start;
                        const pEnd = b.billing_period_end || b.period_end || b.service_end;
                        return (
                          <div style={{ color: '#9ca3af' }}>
                            <strong>{pStart} → {pEnd}</strong>
                            <span style={{ margin: '0 8px' }}>•</span>
                            Bill: <strong>{fmt(billAmt)}</strong>
                            <span style={{ margin: '0 8px' }}>•</span>
                            <span style={{ color: color }}>Diff: {fmt(diff)}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Section 4: Tax Bill (Taxes Only) */}
          {isTaxes && (
            <div style={{ marginBottom:20 }}>
              <label className="form-label" htmlFor="tax-bill-select">Tax Bill (optional)</label>
              <select
                id="tax-bill-select"
                className="form-select"
                disabled={true}
                value=""
              >
                <option value="">-- No tax bills available --</option>
              </select>
              <small className="form-text text-muted">Linking to specific Tax Bill objects is not yet supported.</small>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btn-success" onClick={handleSave} disabled={busy || (!selectedAssetId && !effectivelyUnlinkedAsset)}>
              {busy ? (isEdit ? 'Updating…' : 'Saving…') : (isEdit ? 'Update Link' : 'Save Link')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
